import type { LoanSummary } from 'tio-common/types/loan-summary';
import { simulateSchedule } from 'tio-employee/demo/repayment-plan-simulator';
import {
  demoScenarioForSelection,
  getDemoDebtLevel,
  type DemoDebtLevelId,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';

export const DEMO_PERSON_LOAN_ID = 'demo-person-loan-1';

export const DEMO_LOAN_ORIGINAL_DATE = '2019-09-01';

export const DEMO_FEDERAL_LOAN_TIME = 'after-october-2007';

export const DEMO_LOAN_NAME = 'Direct Consolidated Unsubsidized';

export const DEMO_LOAN_SERVICER = 'MOHELA';

export const DEMO_LOAN_ACCOUNT_NUMBER = '****2210';

// Dollars; the principal/interest split of the current balance on every loan surface.
export function demoLoanBalanceSplit(balanceDollars: number) {
  const principalDollars = Math.round(balanceDollars * 0.94);
  return { principalDollars, interestDollars: balanceDollars - principalDollars };
}

// tio_types.enums rows as tio-api's db/seeds/fixtures/dev JSON seeds them on every deploy.
const STANDARD_REPAYMENT_PLAN = {
  derived: 'RepaymentPlan.STANDARD',
  description: 'STANDARD REPAYMENT',
  planKey: 'tenYearsStandard',
};

type DemoLoanPlan = typeof STANDARD_REPAYMENT_PLAN;

// The row derive_repayment_plan_from_raw matches a servicer's INCOME-BASED REPAYMENT to.
export const DEMO_PSLF_LOAN_PLAN: DemoLoanPlan = {
  derived: 'RepaymentPlan.IBR',
  description: 'INCOME-BASED REPAYMENT',
  planKey: 'ibr',
};

export const DEMO_LOAN_STATUS = { derived: 'LoanStatus.REPAYMENT', description: 'Repayment' };

// The RepaymentPlan row for each debt-axis plan label, and the plan key summarize_loans normalizes its dot to.
const DEMO_PLAN_BY_DEBT_PLAN: Record<string, DemoLoanPlan> = {
  Standard: STANDARD_REPAYMENT_PLAN,
};

export const demoPslfActive = (selection: DemoSessionSelectionSnapshot) =>
  demoScenarioForSelection(selection).features.pslf;

// The person-graph loan's dot and the loan summary's plan key, from one source; a PSLF borrower is on IBR.
export const demoLoanPlan = (debtPlan: string, pslf = false) =>
  pslf ? DEMO_PSLF_LOAN_PLAN : (DEMO_PLAN_BY_DEBT_PLAN[debtPlan] ?? STANDARD_REPAYMENT_PLAN);

// Every enum row a demo loan can carry, one per dot.
export function demoLoanEnumRows() {
  const rows = [
    ...Object.values(DEMO_PLAN_BY_DEBT_PLAN),
    STANDARD_REPAYMENT_PLAN,
    DEMO_PSLF_LOAN_PLAN,
    DEMO_LOAN_STATUS,
  ].map(({ derived, description }) => ({ dot: derived, description }));
  return rows.filter((row, index) => rows.findIndex((other) => other.dot === row.dot) === index);
}

// The NSLDS qualifying-payment count the PSLF tracker shows.
export const DEMO_PSLF_QUALIFYING_PAYMENTS = 78;

export const demoLoanOriginalAmountDollars = (balanceDollars: number) =>
  Math.round(balanceDollars * 1.15);

// Cents and a decimal-fraction rate, as tio-api users_controller#summarize_loans returns them.
export function buildDemoLoanSummary(debtLevelId: DemoDebtLevelId, pslf = false): LoanSummary {
  const debt = getDemoDebtLevel(debtLevelId);
  if (
    !debt.hasLinkedLoans ||
    debt.balanceDollars === null ||
    debt.balanceCents === null ||
    debt.monthlyPaymentCents === null
  ) {
    return { summaryByLoanType: [], errors: [] };
  }
  const plan = demoLoanPlan(debt.repaymentPlan, pslf).planKey;
  return {
    summaryByLoanType: [
      {
        loanType: 'direct',
        loanIds: [DEMO_PERSON_LOAN_ID],
        currentBalance: debt.balanceCents,
        interestRate: Math.round(debt.interestRate * 100) / 10000,
        monthlyPayment: demoMonthlyPaymentCents(debtLevelId, pslf) ?? debt.monthlyPaymentCents,
        repaymentPlan: plan,
        repaymentPlans: [plan],
        totalDisbursedAmount: demoLoanOriginalAmountDollars(debt.balanceDollars) * 100,
        federalLoanTime: DEMO_FEDERAL_LOAN_TIME,
      },
    ],
    errors: [],
  };
}

export interface DemoHousehold {
  agiCents: number;
  numberOfAdults: number;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  madePayments: number;
  affordPayments: string;
}

const HOUSEHOLD_BASE = {
  firstLoanTime: 'new_ibr_cohort',
  lastLoanTime: 'pre_obbb_cohort',
  affordPayments: 'no',
};

const HOUSEHOLDS: Record<Exclude<DemoDebtLevelId, 'not-linked'>, DemoHousehold> = {
  low: {
    ...HOUSEHOLD_BASE,
    agiCents: 4_850_000,
    numberOfAdults: 1,
    numberOfChildren: 0,
    madePayments: 36,
  },
  medium: {
    ...HOUSEHOLD_BASE,
    agiCents: 6_240_000,
    numberOfAdults: 2,
    numberOfChildren: 1,
    madePayments: 48,
  },
  high: {
    ...HOUSEHOLD_BASE,
    agiCents: 9_600_000,
    numberOfAdults: 2,
    numberOfChildren: 2,
    madePayments: 60,
  },
};

export function demoHousehold(debtLevelId: DemoDebtLevelId): DemoHousehold {
  const level = getDemoDebtLevel(debtLevelId).id;
  return { ...(level === 'not-linked' ? HOUSEHOLDS.medium : HOUSEHOLDS[level]) };
}

// Cents; a PSLF borrower pays the IBR amount the estimators simulate, everyone else the debt level's.
export function demoMonthlyPaymentCents(debtLevelId: DemoDebtLevelId, pslf: boolean) {
  const debt = getDemoDebtLevel(debtLevelId);
  if (!pslf || debt.balanceCents === null || debt.balanceDollars === null) {
    return debt.monthlyPaymentCents;
  }
  const household = demoHousehold(debtLevelId);
  const schedule = simulateSchedule({
    payment_plan: DEMO_PSLF_LOAN_PLAN.planKey,
    interest_rate_annual: debt.interestRate / 100,
    current_balance: debt.balanceCents,
    original_disbursement_amount: demoLoanOriginalAmountDollars(debt.balanceDollars) * 100,
    number_of_adults: household.numberOfAdults,
    number_of_children: household.numberOfChildren,
    agi: household.agiCents,
    first_loan_time: household.firstLoanTime,
    loan_type: 'direct',
    paid_months: DEMO_PSLF_QUALIFYING_PAYMENTS,
    pslf: true,
  });
  return ('rows' in schedule ? schedule.rows[0]?.payment : undefined) ?? debt.monthlyPaymentCents;
}
